import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { get, post, put, del } from '../../../shared/utils/api.js';

export const useUserStore = defineStore('user', () => {
  // 状态
  const user = ref(null);
  const store = ref(null);
  const cart = ref([]);
  const orders = ref([]);
  const addresses = ref([]);
  
  // 订单状态管理
  const orderType = ref('delivery'); // 'dine_in' 或 'delivery'
  const tableCode = ref(null);
  const storeId = ref(1);
  
  // 计算属性
  const cartTotal = computed(() => {
    return cart.value.reduce((total, item) => total + Number(item.quantity || 0), 0);
  });
  
  const cartTotalPrice = computed(() => {
    return cart.value.reduce((total, item) => total + (item.price * item.quantity), 0);
  });
  
  const cartTotalQuantity = computed(() => {
    return cart.value.reduce((total, item) => total + Number(item.quantity || 0), 0);
  });
  
  // 订单状态相关计算属性
  const isDineIn = computed(() => orderType.value === 'dine_in');
  const isDelivery = computed(() => orderType.value === 'delivery');
  const orderTypeText = computed(() => {
    return orderType.value === 'dine_in' ? '扫码点餐' : '外卖配送';
  });
  
  // 用户相关方法
  const wechatLogin = async (code, tempDeviceId) => {
    try {
      const result = await post('/user/wechat-login', { 
        code,
        device_id: tempDeviceId  // 仅用于关联账号，不用于登录
      });
      if (result.success) {
        user.value = result.data.user;
        // 保存token到localStorage
        if (result.data.token) {
          localStorage.setItem('token', result.data.token);
        }
        // 保存用户信息到本地存储
        saveUserToLocal();
        return result;
      }
      return result;
    } catch (error) {
      throw error;
    }
  };
  
  const getWechatAuthUrl = async (redirectUrl = null, scope = 'snsapi_base') => {
    try {
      const params = { scope };
      if (redirectUrl) {
        params.redirect_url = redirectUrl;
      }
      const result = await get('/user/wechat-auth-url', params);
      return result;
    } catch (error) {
      throw error;
    }
  };
  
  const getWechatPhone = async (code) => {
    try {
      const result = await post('/user/get-phone', { code });
      if (result.success) {
        // 更新本地用户信息中的手机号
        if (user.value) {
          user.value.phone = result.data.phone;
          saveUserToLocal();
        }
      }
      return result;
    } catch (error) {
      throw error;
    }
  };
  
  const getUserProfile = async (forceRefresh = false) => {
    try {
      // 如果不强制刷新且本地有缓存，直接返回本地数据
      if (!forceRefresh && user.value) {
        return { success: true, data: user.value };
      }
      
      const result = await get('/user/profile');
      if (result.success) {
        user.value = result.data;
        // 保存到本地存储
        saveUserToLocal();
      }
      return result;
    } catch (error) {
      throw error;
    }
  };
  
  const updateUserProfile = async (userData) => {
    try {
      const result = await put('/user/profile', userData);
      if (result.success) {
        user.value = { ...user.value, ...userData };
        // 保存到本地存储
        saveUserToLocal();
      }
      return result;
    } catch (error) {
      throw error;
    }
  };
  
  // 门店相关方法
  const getStoreInfo = async (storeId) => {
    try {
      const result = await get(`/common/store/${storeId}`);
      if (result.success) {
        store.value = result.data;
        return result.data;
      }
      return null;
    } catch (error) {
      throw error;
    }
  };
  
  const getStoreCategories = async (storeId) => {
    try {
      const result = await get(`/common/store/${storeId}/categories`);
      return result;
    } catch (error) {
      throw error;
    }
  };
  
  const getStoreProducts = async (storeId, params = {}) => {
    try {
      // 过滤掉null和undefined值
      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(([key, value]) => value !== null && value !== undefined)
      );
      const queryString = new URLSearchParams(filteredParams).toString();
      const url = `/common/store/${storeId}/products${queryString ? '?' + queryString : ''}`;
      const result = await get(url);
      return result;
    } catch (error) {
      throw error;
    }
  };
  
  const getProductInfo = async (storeId, productId) => {
    try {
      const result = await get(`/common/store/${storeId}/product/${productId}`);
      return result;
    } catch (error) {
      throw error;
    }
  };
  
  // 订单状态管理方法
  const setOrderType = (type, tableCodeValue = null, storeIdValue = 1) => {
    orderType.value = type;
    tableCode.value = tableCodeValue;
    storeId.value = storeIdValue;
    
    // 保存到localStorage
    localStorage.setItem('orderType', type);
    localStorage.setItem('tableCode', tableCodeValue || '');
    localStorage.setItem('storeId', storeIdValue.toString());
  };
  
  const loadOrderState = () => {
    const savedOrderType = localStorage.getItem('orderType');
    const savedTableCode = localStorage.getItem('tableCode');
    const savedStoreId = localStorage.getItem('storeId');
    
    if (savedOrderType) {
      orderType.value = savedOrderType;
    }
    if (savedTableCode) {
      tableCode.value = savedTableCode;
    }
    if (savedStoreId) {
      storeId.value = parseInt(savedStoreId);
    }
  };

  // 初始化本地缓存数据（移除用户信息缓存）
  const initializeLocalData = () => {
    loadOrderState();
    loadCartFromLocal();
    loadAddressesFromLocal();
  };
  
  const clearOrderState = () => {
    orderType.value = 'delivery';
    tableCode.value = null;
    storeId.value = 1;
    localStorage.removeItem('orderType');
    localStorage.removeItem('tableCode');
    localStorage.removeItem('storeId');
  };
  
  // 购物车相关方法
  const addToCart = (product) => {
    const existingItem = cart.value.find(item => 
      item.product_id === product.product_id && 
      item.spec_id === product.spec_id
    );
    
    if (existingItem) {
      existingItem.quantity += product.quantity;
    } else {
      cart.value.push({ ...product });
    }
    
    // 保存到本地存储
    saveCartToLocal();
  };
  
  const saveCartToLocal = () => {
    localStorage.setItem('cart', JSON.stringify(cart.value));
  };
  
  const loadCartFromLocal = () => {
    try {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        cart.value = JSON.parse(savedCart);
      }
    } catch (error) {
      console.error('加载购物车失败:', error);
      cart.value = [];
    }
  };

  // 用户信息本地存储方法
  const saveUserToLocal = () => {
    if (user.value) {
      localStorage.setItem('user', JSON.stringify(user.value));
    }
  };

  const loadUserFromLocal = () => {
    try {
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        user.value = JSON.parse(savedUser);
        return true;
      }
    } catch (error) {
      console.error('加载用户信息失败:', error);
    }
    return false;
  };

  // 地址信息本地存储方法
  const saveAddressesToLocal = () => {
    if (addresses.value) {
      localStorage.setItem('addresses', JSON.stringify(addresses.value));
    }
  };

  const loadAddressesFromLocal = () => {
    try {
      const savedAddresses = localStorage.getItem('addresses');
      if (savedAddresses) {
        addresses.value = JSON.parse(savedAddresses);
        return true;
      }
    } catch (error) {
      console.error('加载地址信息失败:', error);
    }
    return false;
  };
  
  const removeFromCart = (index) => {
    cart.value.splice(index, 1);
    saveCartToLocal();
  };
  
  const updateCartQuantity = (index, quantity) => {
    if (quantity <= 0) {
      cart.value.splice(index, 1);
    } else {
      cart.value[index].quantity = quantity;
    }
    saveCartToLocal();
  };
  
  const clearCart = () => {
    cart.value = [];
    localStorage.removeItem('cart');
  };
  
  // 订单相关方法
  const createOrder = async (orderData) => {
    try {
      const result = await post('/user/orders', orderData);
      if (result.success) {
        // 不在这里清空购物车，由调用方决定何时清空
        // 因为可能需要等待支付完成
        const { has_wechat_pay, payment_params, payment_error } = result.data;
        
        // 返回增强的结果
        return {
          success: true,
          data: result.data,
          needPayment: has_wechat_pay && payment_params,
          paymentParams: payment_params,
          paymentError: payment_error
        };
      }
      return result;
    } catch (error) {
      throw error;
    }
  };
  
  const getUserOrders = async (params = {}) => {
    try {
      const result = await get('/user/orders', params);
      if (result.success) {
        orders.value = result.data;
      }
      return result;
    } catch (error) {
      throw error;
    }
  };
  
  const getOrderDetail = async (orderId) => {
    try {
      const result = await get(`/user/orders/${orderId}`);
      return result;
    } catch (error) {
      throw error;
    }
  };
  
  // 地址相关方法
  const getUserAddresses = async (forceRefresh = false) => {
    try {
      // 如果不强制刷新且本地有缓存，直接返回本地数据
      // if (!forceRefresh && addresses.value && addresses.value.length > 0) {
      //   return { success: true, data: addresses.value };
      // }
      
      const result = await get('/user/addresses');
      if (result.success) {
        addresses.value = result.data;
        // 保存到本地存储
        saveAddressesToLocal();
      }
      return result;
    } catch (error) {
      throw error;
    }
  };
  
  const addAddress = async (addressData) => {
    try {
      const result = await post('/user/addresses', addressData);
      if (result.success) {
        await getUserAddresses(true); // 强制刷新地址列表
        // 保存地址到本地存储
        saveAddressesToLocal();
      }
      return result;
    } catch (error) {
      throw error;
    }
  };
  
  const updateAddress = async (addressId, addressData) => {
    try {
      const result = await put(`/user/addresses/${addressId}`, addressData);
      if (result.success) {
        await getUserAddresses(true); // 强制刷新地址列表
        // 保存地址到本地存储
        saveAddressesToLocal();
      }
      return result;
    } catch (error) {
      throw error;
    }
  };
  
  const deleteAddress = async (addressId) => {
    try {
      const result = await del(`/user/addresses/${addressId}`);
      if (result.success) {
        await getUserAddresses(true); // 强制刷新地址列表
        // 保存地址到本地存储
        saveAddressesToLocal();
      }
      return result;
    } catch (error) {
      throw error;
    }
  };
  
  const setDefaultAddress = async (addressId) => {
    try {
      const result = await post(`/user/addresses/${addressId}/set-default`);
      if (result.success) {
        await getUserAddresses(true); // 强制刷新地址列表
        // 保存地址到本地存储
        saveAddressesToLocal();
      }
      return result;
    } catch (error) {
      throw error;
    }
  };
  
  return {
    // 状态
    user,
    store,
    cart,
    orders,
    addresses,
    orderType,
    tableCode,
    storeId,
    
    // 计算属性
    cartTotal,
    cartTotalPrice,
    cartTotalQuantity,
    isDineIn,
    isDelivery,
    orderTypeText,
    
    // 方法
    wechatLogin,
    getWechatAuthUrl,
    getWechatPhone,
    getUserProfile,
    updateUserProfile,
    getStoreInfo,
    getStoreCategories,
    getStoreProducts,
    getProductInfo,
    setOrderType,
    loadOrderState,
    clearOrderState,
    initializeLocalData,
    addToCart,
    removeFromCart,
    updateCartQuantity,
    clearCart,
    saveCartToLocal,
    loadCartFromLocal,
    saveUserToLocal,
    loadUserFromLocal,
    saveAddressesToLocal,
    loadAddressesFromLocal,
    createOrder,
    getUserOrders,
    getOrderDetail,
    getUserAddresses,
    addAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress
  };
});


